import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import { useForm, Controller } from 'react-hook-form'
import LayoutSidebar from '../../../components/LayoutSidebar'
import { selectAuth } from "../../../redux/authSlice"
import { selectModules } from '../../../redux/moduleSlice'
import { useLocation, useNavigate } from "react-router-dom"
import Loading from '../../../components/Loading'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import { MODULE_FIELD_TYPES } from '../../../constants'
import { selectUser } from '../../../redux/userSlice'
import { hasUserPermission } from '../../../utils/permissionHelper'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye } from '@fortawesome/free-solid-svg-icons'
import { setModal } from '../../../redux/modalSlice'

export default function CustomerMergeTool(props) {
  const auth = useSelector(selectAuth)
  const request = useRequest()
  const dispatch = useDispatch()
  const location = useLocation()
  const { handleSubmit, control, setError, formState: { errors } } = useForm();
  const [responseData, setResponseData] = useState([])
  const query = new URLSearchParams(location.search)
  const sourceCustomerId = query.get('scid')
  const targetCustomerId = query.get('tcid')
  const [sourceCustomer, setSourceCustomer] = useState({})
  const [targetCustomer, setTargetCustomer] = useState({})
  const [module, setModule] = useState()
  const active = useSelector(selectUser)
  const modules = useSelector(selectModules)
  const viewOnlyFields = ['id', 'guid', 'created_at', 'created_by', 'updated_at', 'updated_by', 'deleted_at', 'deleted_by'];
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const [isDisplayAffectedRecords, setIsDisplayAffectedRecords] = useState(false)
  const [userPermissions, setUserPermissions] = useState()
  const navi = useNavigate()

  useEffect(() => {
    if (active.status === 'succeeded') {
      const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
      setUserPermissions(activePermissions)
      if (!hasUserPermission(activePermissions, `merge_customer`)) {
        navi('/403')
      }
    }
  }, [active])

  useEffect(() => {
    if (!auth.fromPageChange) { return }
    if (auth.accessToken) {
      const m = modules.data.find((e) => e.code_key === 'customer')
      setModule(m)
      getCustomerInformation()
    }
  }, [auth.accessToken])

  const getCustomerInformation = async () => {
    setLoaded(false)
    try {
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer_merge_records?source_customer_id=${sourceCustomerId}&target_customer_id=${targetCustomerId}`)
      const response = await request.get()
      setResponseData(response?.data?.data);
      setSourceCustomer(response?.data?.data?.source_customer?.info)
      setTargetCustomer(response?.data?.data?.target_customer?.info)
      setLoaded(true)
    } catch (error) {
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
      setLoaded(true)
    }
  }

  const renderHeader = (column_name) => {
    const data = module.module_fields.find(
      (header) => header.column_name === column_name
    )
    if (data?.label_name) {
      return data?.label_name
    } else {
      return column_name;
    }
  }

  const renderEncryptedFieldChallenge = (key, value, customerId, isDifferent) => {
    return (
      <>
        {value}
        {isDifferent && (
          <a className="ml-2">
            <FontAwesomeIcon
              icon={faEye}
              onClick={() => dispatch(setModal({
                is_active: true,
                type: 'encrypted_field_challenge_modal',
                options: {
                  title: renderHeader(key.column_name),
                  customerId: customerId,
                  fieldName: key.column_name
                }
              }))}
            />
          </a>
        )}
      </>
    );
  };

  const renderData = (customer, key, fieldType, isDifferent) => {
    let value = ''
    switch (fieldType) {
      case MODULE_FIELD_TYPES.DROPDOWN_SYSTEM.code_key:
        value = dropdownOptions?.find((x) => x?.id == customer[key.column_name])?.name
        break
      case MODULE_FIELD_TYPES.ENCRYPTED.code_key:
        if (key.column_name == 'drivers_license_number') {
          if (customer[key.column_name] == false) {
            break;
          } else {
            const max_length = key.max_length ? key.max_length : 10
            value = '#'.repeat(max_length)
            value = renderEncryptedFieldChallenge(key, value, customer.id, isDifferent)
          }
        } else {
          const last_four = customer[key.column_name + '_last_four']
          const max_length = key.max_length ? key.max_length : 10
          if (last_four) {
            value = '#'.repeat(max_length - 4) + last_four
          } else {
            value = '#'.repeat(max_length)
          }
          value = renderEncryptedFieldChallenge(key, value, customer.id, isDifferent)
        }
        break
      case MODULE_FIELD_TYPES.BOOLEAN.code_key:
        if (customer[key.column_name] == 1) {
          value = 'Yes'
        } else if (customer[key.column_name] == 0) {
          value = 'No'
        }
        break
      case MODULE_FIELD_TYPES.TAG_CLOUD.code_key:
        value = (!customer[key.column_name] || customer[key.column_name].length === 0) ? '-' : (
          <span className="tags pt-1 pb-0">
            {customer[key.column_name].map((item, index) => (
              <span className="tag is-link is-rounded" style={{ marginBottom: "0.4rem", padding: "0.5em", height: "1.2em", }} key={item.id}>{item.name}</span>
            ))}
          </span>
        )
        break
      case MODULE_FIELD_TYPES.DROPDOWN_SEARCHABLE.code_key:
      case MODULE_FIELD_TYPES.DROPDOWN.code_key:
        const field_label = key.column_name.replace('_id', '_label')
        value = customer[field_label];
        break
      default:
        value = customer[key.column_name]
    }

    return value
  }

  const checkDifference = (sourceCustomer, targetCustomer, isViewOnly, key) => {
    let difference = false
    const fieldType = dropdownOptions[key.module_field_type_dropdown_option_id] && dropdownOptions[key.module_field_type_dropdown_option_id].code_key

    switch (fieldType) {
      case MODULE_FIELD_TYPES.ENCRYPTED.code_key:
        difference = sourceCustomer[key.column_name + '_encrypted'] !== targetCustomer[key.column_name + '_encrypted'] && !isViewOnly
        break
      default:
        difference = sourceCustomer[key.column_name] !== targetCustomer[key.column_name] && !isViewOnly
    }
    return difference
  }

  const onSubmit = async (formData) => {
    if (isDisplayAffectedRecords) {
      setSaving(true)
      request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/customer_merge_records`)
      try {
        let body = {}
        body.source_customer_id = sourceCustomerId
        body.target_customer_id = targetCustomerId
        body.merge_fields = formData

        const response = await request.post(body)
        if (response.status === 200 || response.status === 201 || response.status === 204) {
          setSaving(false)
          dispatch(setNotification({ type: 'success', body: response?.data?.message }))
          setIsDisplayAffectedRecords(false)
          // setResponseData([])
          setTimeout(() => {
            navi(`/customer/${responseData?.good_customer_id}`, { state: { reloadPage: true } })
          }, 1500)
        } else {
          setSaving(false)
          dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
        }
      } catch (error) {
        setSaving(false)
        const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
        dispatch(setNotification({ type: 'error', body: errorMessage }))
      }
    } else {
      let numberOfErrors = 0;
      Object.keys(formData).forEach((key) => {
        if (formData[key] == "") {
          setError(key, {
            type: "manual",
            message: "You must choose which value to keep"
          });
          numberOfErrors++;
        }
      });

      if (numberOfErrors > 0) {
        setIsDisplayAffectedRecords(false)
      } else {
        setIsDisplayAffectedRecords(true)
      }
    }
  };

  useEffect(() => {
    if (isDisplayAffectedRecords) {
      dispatch(
        setModal({
          is_active: true,
          type: 'are_you_sure',
          options: {
            title: "Confirm Merge",
            description:
              <div className="columns mb-0">
                <div className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start">
                  {getAffectedRecords()}
                </div>
              </div>,
            actionLabel: "Merge",
            callMethod: () => handleSubmit(onSubmit)(),
            closeMethod: () => setIsDisplayAffectedRecords(false)
          }
        })
      )
    }
  }, [isDisplayAffectedRecords]);

  const getAffectedRecords = () => {
    let badCustomerRecords;

    if (responseData) {
      const sourceTotal = responseData.source_customer?.total_foreign_keys || 0;
      const targetTotal = responseData.target_customer?.total_foreign_keys || 0;

      badCustomerRecords = sourceTotal >= targetTotal ? responseData.target_customer : responseData.source_customer;
    }

    return (
      badCustomerRecords && (
        <>
          <p>Affected Related Records</p>
          <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-fullwidth is-hoverable mb-0">
            <tbody>
              <tr>
                <td>Leads</td>
                <td>{responseData.source_customer.foreign_key?.count_leads + responseData.target_customer.foreign_key?.count_leads}</td>
              </tr>
              <tr>
                <td>Loans</td>
                <td>{responseData.source_customer.foreign_key?.count_loans + responseData.target_customer.foreign_key?.count_loans}</td>
              </tr>
              <tr>
                <td>Payments</td>
                <td>{responseData.source_customer.foreign_key?.count_payments + responseData.target_customer.foreign_key?.count_payments}</td>
              </tr>
              <tr>
                <td>Notes</td>
                <td>{responseData.source_customer.foreign_key?.count_customer_notes + responseData.target_customer.foreign_key?.count_customer_notes}</td>
              </tr>
              <tr>
                <td>Promotions</td>
                <td>{responseData.source_customer.foreign_key?.count_customer_promotion_codes + responseData.target_customer.foreign_key?.count_customer_promotion_codes}</td>
              </tr>
              <tr>
                <td>Debit Cards</td>
                <td>{responseData.source_customer.foreign_key?.count_customer_debit_cards + responseData.target_customer.foreign_key?.count_customer_debit_cards}</td>
              </tr>
              <tr>
                <td>Surveys</td>
                <td>{responseData.source_customer.foreign_key?.count_customer_surveys + responseData.target_customer.foreign_key?.count_customer_surveys}</td>
              </tr>
              <tr>
                <td>Documents</td>
                <td>{responseData.source_customer.foreign_key?.count_documents + responseData.target_customer.foreign_key?.count_documents}</td>
              </tr>
              <tr>
                <td>Email Messages</td>
                <td>{responseData.source_customer.foreign_key?.count_customer_email_messages + responseData.target_customer.foreign_key?.count_customer_email_messages}</td>
              </tr>
              <tr>
                <td>SMS Messages</td>
                <td>{responseData.source_customer.foreign_key?.count_customer_sms_messages + responseData.target_customer.foreign_key?.count_customer_sms_messages}</td>
              </tr>
              <tr>
                <td>Manager Approvals</td>
                <td>{responseData.source_customer.foreign_key?.count_manager_approvals + responseData.target_customer.foreign_key?.count_manager_approvals}</td>
              </tr>
              <tr>
                <td>Power of Attorneys</td>
                <td>{responseData.source_customer.foreign_key?.count_power_of_attorney_customers + responseData.target_customer.foreign_key?.count_power_of_attorney_customers}</td>
              </tr>
              <tr>
                <td>Bankruptcy Monitorings</td>
                <td>{responseData.source_customer.foreign_key?.count_bankruptcy_monitorings + responseData.target_customer.foreign_key?.count_bankruptcy_monitorings}</td>
              </tr>
              <tr>
                <td>Rewards</td>
                <td>{responseData.source_customer.foreign_key?.count_customer_rewards + responseData.target_customer.foreign_key?.count_customer_rewards}</td>
              </tr>
              <tr>
                <td>Cease and Deceased</td>
                <td>{responseData.source_customer.foreign_key?.count_cease_and_desists + responseData.target_customer.foreign_key?.count_cease_and_desists}</td>
              </tr>
              <tr>
                <td>RC Interactions</td>
                <td>{responseData.source_customer.foreign_key?.count_rc_interactions + responseData.target_customer.foreign_key?.count_rc_interactions}</td>
              </tr>
            </tbody>
          </table>
        </>
      )
    )
  }

  return (
    <LayoutSidebar
      contentTitle={'Customer Merge Tool'}
      content={(
        <div className="lox-detail mt-1 mx-1">
          <div className="columns mb-0">
            <div className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start">
              <div className="title is-4 has-text-primary is-flex">Merge Records</div>
              <p className="subtitle is-6">INSTRUCTIONS: In the case of different data, choose the value you want the final record to contain.</p>
            </div>
          </div>

          {!loaded && responseData.length === 0 && (
            <Loading />
          )}

          {responseData.length !== 0 && (
            <>
              <div className="columns mb-0">
                <div className="column is-flex is-flex-direction-column is-justify-content-center is-align-items-flex-start">
                  <p className="subtitle is-6">
                    Record 1: {sourceCustomer?.guid} {responseData?.source_customer?.info?.id == responseData?.good_customer_id ? '(Good Record)' : ''}<br />
                    Record 2: {targetCustomer?.guid} {responseData?.target_customer?.info?.id == responseData?.good_customer_id ? '(Good Record)' : ''}
                  </p>
                </div>
              </div>
              <form onSubmit={handleSubmit(onSubmit)} id="customer_merge_form">
                <div className="table-container mb-0">
                  <table className="lox-hooktable-table table is-bordered is-striped is-narrow is-fullwidth is-hoverable mb-3">
                    <thead>
                      <tr>
                        <th className="is-selected has-text-centered">Field</th>
                        <th className="is-selected has-text-centered">{sourceCustomer?.first_name + " " + sourceCustomer?.last_name + " - " + sourceCustomer?.guid}</th>
                        <th className="is-selected has-text-centered">Keep Value From</th>
                        <th className="is-selected has-text-centered">{targetCustomer?.first_name + " " + targetCustomer?.last_name + " - " + targetCustomer?.guid}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {responseData && module?.module_fields?.map((key) => {
                        const columnName = key.column_name;
                        const isViewOnly = viewOnlyFields.includes(columnName);
                        const isDifferent = checkDifference(sourceCustomer, targetCustomer, isViewOnly, key);
                        const fieldType = dropdownOptions[key.module_field_type_dropdown_option_id] && dropdownOptions[key.module_field_type_dropdown_option_id].code_key

                        if (![MODULE_FIELD_TYPES.SUBHEADER.code_key, MODULE_FIELD_TYPES.TAG_CLOUD.code_key, MODULE_FIELD_TYPES.SHOWMORE.code_key].includes(fieldType)) {
                          return (
                            <tr key={columnName}>
                              <td className="has-text-left">{renderHeader(columnName)}</td>
                              <td className={`has-text-left ${isDifferent ? 'has-text-danger' : ''}`}>{renderData(sourceCustomer, key, fieldType, isDifferent)}</td>
                              <td className="has-text-centered">
                                <div className="is-flex is-justify-content-space-between">
                                  {isDifferent && (
                                    <div>
                                      <Controller
                                        name={columnName}
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                          <input {...field} type="radio" value={sourceCustomerId} />
                                        )}
                                      />
                                    </div>
                                  )}
                                  <div>
                                    {isDifferent && (
                                      <Controller
                                        name={columnName}
                                        control={control}
                                        defaultValue=""
                                        render={({ field }) => (
                                          <input {...field} type="radio" value={targetCustomerId} />
                                        )}
                                      />
                                    )}
                                  </div>
                                </div>
                                <p className="has-text-danger is-size-7">
                                  {errors[columnName] && <span>{errors[columnName].message}</span>}
                                </p>
                              </td>
                              <td className={`has-text-left ${isDifferent ? 'has-text-danger' : ''}`}>{renderData(targetCustomer, key, fieldType, isDifferent)}</td>
                            </tr>
                          );
                        }
                      })}
                    </tbody>
                    <thead>
                      <tr>
                        <th className="is-selected has-text-left has-background-grey-special">Leads</th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.source_customer?.foreign_key?.count_leads}</th>
                        <th className="is-selected has-text-left has-background-grey-special"></th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.target_customer?.foreign_key?.count_leads}</th>
                      </tr>
                      <tr>
                        <th className="is-selected has-text-left has-background-grey-special">Loans</th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.source_customer?.foreign_key?.count_loans}</th>
                        <th className="is-selected has-text-left has-background-grey-special"></th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.target_customer?.foreign_key?.count_loans}</th>
                      </tr>
                      <tr>
                        <th className="is-selected has-text-left has-background-grey-special">Payments</th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.source_customer?.foreign_key?.count_payments}</th>
                        <th className="is-selected has-text-left has-background-grey-special"></th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.target_customer?.foreign_key?.count_payments}</th>
                      </tr>
                      <tr>
                        <th className="is-selected has-text-left has-background-grey-special">Notes</th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.source_customer?.foreign_key?.count_customer_notes}</th>
                        <th className="is-selected has-text-left has-background-grey-special"></th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.target_customer?.foreign_key?.count_customer_notes}</th>
                      </tr>
                      <tr>
                        <th className="is-selected has-text-left has-background-grey-special">Promotions</th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.source_customer?.foreign_key?.count_customer_promotion_codes}</th>
                        <th className="is-selected has-text-left has-background-grey-special"></th>
                        <th className="is-selected has-text-left has-background-grey-special">{responseData?.target_customer?.foreign_key?.count_customer_promotion_codes}</th>
                      </tr>
                    </thead>
                  </table>
                </div>

                <div className={`has-text-right mb-2`}>
                  <button type="submit" className="button is-link is-small is-small" disabled={isDisplayAffectedRecords && saving}>
                    <span className="mx-3">Continue</span>
                  </button>
                </div>

              </form>
            </>
          )}
        </div>
      )}
    />
  )
}
